import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Clock, Download, ExternalLink, FileText, FolderInput, Image as ImageIcon, Landmark,
  Layers, Loader2, Stamp, Tag as TagIcon, Trash2, Users, X,
} from 'lucide-react';
import api from '../../lib/api';
import { cn } from '../../lib/cn';
import {
  Button, Card, ConfirmModal, EmptyState, IconButton, Input, Modal, Select, Skeleton, Tag, ToolbarChip, ToolbarGroup,
} from '../ui';
import { PartyBadge, getPartyLabel, type SourceParty } from '../AccountCard';
import { docTypeGroup, docTypeLabel, reconciliationBadge } from '../../lib/enums';
import { formatAccountName } from '../../utils/accounts';
import { formatTxnDate } from '../../utils/dates';

// The Documents tree is rooted on FILES AS THEY WERE UPLOADED (the immutable
// originals in the document registry). A bundle or portfolio PDF that was
// split into several statements is ONE node here, with its extracted
// statements nested beneath it — the attorney always finds the original.

interface DocStatement {
  id: number;
  bank_label: string | null;
  account_last4: string | null;
  account_type: string | null;
  owner_type: string | null;
  statement_start_date: string | null;
  statement_end_date: string | null;
  status: string | null;
  reconciliation_status: string | null;
  bates_start: string | null;
  bates_end: string | null;
}

/** Document.bates payload: a detected scan (server/bates.py), a range typed
 *  at upload ("manual"), or a slice derived from a parent production. */
interface DocBates {
  source?: 'detected' | 'manual' | 'derived';
  start?: string | null;
  end?: string | null;
  contiguous?: boolean;
  gaps?: [string, string][];
  detected_differs?: string;
}

interface DocProposal {
  id: number;
  doc_type: string;
  status: string;
}

/** Discovery Production Tracker round assignment on a produced document. */
interface DocProductionRound {
  round_id: number;
  round_number: number;
  label: string | null;
  supplement: number | null;
}

interface UploadedDocument {
  id: number;
  filename: string;
  content_type: string | null;
  doc_type: string | null;
  source_party: SourceParty;
  uploaded_at: string | null;
  has_original: boolean;
  size_bytes: number | null;
  bates: DocBates | null;
  produced_date?: string | null;
  production_supplement?: number | null;
  production_round: DocProductionRound | null;
  statements: DocStatement[];
  proposals: DocProposal[];
}

function ordinal(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  const suffix = { 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] ?? 'th';
  return `${n}${suffix}`;
}

/** "RFP 2" / custom label, plus " · 1st supp" when the document arrived in a
 *  supplement rather than the original response. */
function productionRoundText(pr: DocProductionRound): string {
  const name = pr.label || `RFP ${pr.round_number}`;
  return pr.supplement ? `${name} · ${ordinal(pr.supplement)} supp` : name;
}

function batesRangeText(b: DocBates): string {
  if (!b.start) return '';
  return b.end && b.end !== b.start ? `${b.start}–${b.end}` : b.start;
}

type GroupMode = 'recent' | 'account' | 'type' | 'producer';

const GROUP_MODES: { value: GroupMode; label: string; icon: typeof Clock }[] = [
  { value: 'recent', label: 'Recent', icon: Clock },
  { value: 'account', label: 'By account', icon: Landmark },
  { value: 'type', label: 'By type', icon: TagIcon },
  { value: 'producer', label: 'By producer', icon: Users },
];

function formatSize(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Date AND time — same-day uploads of identically named files are only
// distinguishable by the clock.
const UPLOADED_AT_FMT = new Intl.DateTimeFormat('en-US', {
  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
});

function formatUploadedAt(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return UPLOADED_AT_FMT.format(d);
}

function statementPeriod(s: DocStatement): string {
  if (s.statement_start_date && s.statement_end_date) {
    return `${formatTxnDate(s.statement_start_date)} – ${formatTxnDate(s.statement_end_date)}`;
  }
  return formatTxnDate(s.statement_start_date || s.statement_end_date) || '';
}

function accountDisplay(s: DocStatement): string {
  return formatAccountName(s.bank_label) || s.bank_label || 'Unknown account';
}

/** Account, then period — the reading order a person expects, instead of
 *  processing order (which interleaves accounts and feels disorganized). */
function sortedStatements(list: DocStatement[]): DocStatement[] {
  return [...list].sort(
    (a, b) =>
      accountDisplay(a).localeCompare(accountDisplay(b)) ||
      (a.statement_start_date ?? '').localeCompare(b.statement_start_date ?? '')
  );
}

/** Fixed section order for the By-type view: case-critical first, junk last —
 *  never alphabetical. Unknown headings sort just before Other. */
const TYPE_SECTION_RANK: Record<string, number> = {
  'Bank & credit card statements': 0,
  'Brokerage & retirement': 1,
  'Tax returns': 2,
  'Pay stubs': 3,
  'Legal documents': 4,
  'Valuations': 5,
  'Mortgage statements': 6,
  'Business interests': 7,
  'Other documents': 99,
};
function typeSectionRank(heading: string): number {
  return TYPE_SECTION_RANK[heading] ?? 50;
}

/** Storage-key uploads carry a UUID filename; show what the document IS and
 *  keep the raw filename as the tooltip. */
const UUID_NAME_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
function displayFilename(doc: UploadedDocument): string {
  if (!UUID_NAME_RE.test(doc.filename)) return doc.filename;
  const first = doc.statements[0];
  if (first) {
    const period = statementPeriod(first);
    return `${accountDisplay(first)} statement${period ? ` (${period})` : ''}`;
  }
  return doc.filename;
}

const STATEMENT_COLLAPSE_THRESHOLD = 8;
const STATEMENT_COLLAPSED_COUNT = 6;

async function fetchOriginal(docId: number, contentType: string | null): Promise<string> {
  const res = await api.get(`/documents/${docId}/pdf`, { responseType: 'blob' });
  const blob = new Blob([res.data], {
    type: res.headers['content-type'] || contentType || 'application/pdf',
  });
  return URL.createObjectURL(blob);
}

interface Props {
  caseId: string;
  clientName?: string | null;
  opposingPartyName?: string | null;
}

export default function DocumentsTab({ caseId, clientName, opposingPartyName }: Props) {
  const [groupMode, setGroupMode] = useState<GroupMode>('recent');
  const [busyDocId, setBusyDocId] = useState<number | null>(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<UploadedDocument | null>(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [batesEditDoc, setBatesEditDoc] = useState<UploadedDocument | null>(null);
  const [batesEditStart, setBatesEditStart] = useState('');
  const [batesEditEnd, setBatesEditEnd] = useState('');
  const [assignRoundDoc, setAssignRoundDoc] = useState<UploadedDocument | null>(null);
  const [assignRoundId, setAssignRoundId] = useState('');
  const [assignSupplement, setAssignSupplement] = useState('0');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['documents', caseId],
    queryFn: async () => {
      const res = await api.get(`/cases/${caseId}/documents`);
      return res.data.documents as UploadedDocument[];
    },
  });

  const documents = useMemo(() => data ?? [], [data]);

  // Lightweight read of the Production Tracker aggregate — only to offer the
  // round choices in the assign modal. Tolerant local shape: the full payload
  // is owned by useProductionTracker.ts.
  const { data: trackerData } = useQuery<{
    parties?: {
      producing_party?: string | null;
      rounds?: { id: number; round_number: number; label?: string | null }[];
    }[];
  }>({
    queryKey: ['production-tracker', caseId],
    queryFn: async () => {
      const res = await api.get(`/cases/${caseId}/production-tracker`);
      return res.data;
    },
  });

  // Deleting a document takes its statements and transactions with it, so
  // every downstream view is stale.
  const invalidateAfterDelete = () => {
    queryClient.invalidateQueries({ queryKey: ['documents', caseId] });
    queryClient.invalidateQueries({ queryKey: ['statements'] });
    queryClient.invalidateQueries({ queryKey: ['accounts'] });
    queryClient.invalidateQueries({ queryKey: ['transactions'] });
    queryClient.invalidateQueries({ queryKey: ['discovery-leads'] });
    queryClient.invalidateQueries({ queryKey: ['duplicate-accounts'] });
    // The server also drops the file's intake proposals and unlinks assets'
    // statement provenance — without these, a cached confirm card 404s.
    queryClient.invalidateQueries({ queryKey: ['intakeProposals', caseId] });
    queryClient.invalidateQueries({ queryKey: ['assets', caseId] });
    // Round membership and Bates accounting are computed over documents.
    queryClient.invalidateQueries({ queryKey: ['production-tracker', caseId] });
  };

  const deleteDocument = useMutation({
    mutationFn: async (docId: number) => {
      const { data: res } = await api.delete(`/documents/${docId}`);
      return res;
    },
    onSuccess: invalidateAfterDelete,
  });

  const saveBates = useMutation({
    mutationFn: async (vars: { docId: number; start: string | null; end: string | null }) => {
      const { data: res } = await api.patch(`/documents/${vars.docId}/bates`, {
        start: vars.start,
        end: vars.end,
      });
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', caseId] });
      // Statement Bates slices are re-stamped (or cleared) server-side.
      queryClient.invalidateQueries({ queryKey: ['statements'] });
      // Round Bates accounting unions document payloads.
      queryClient.invalidateQueries({ queryKey: ['production-tracker', caseId] });
    },
  });

  const assignRound = useMutation({
    mutationFn: async (vars: { docId: number; roundId: number | null; supplement: number | null }) => {
      const { data: res } = await api.patch(`/documents/${vars.docId}/production-round`, {
        round_id: vars.roundId,
        supplement: vars.supplement,
      });
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', caseId] });
      queryClient.invalidateQueries({ queryKey: ['production-tracker', caseId] });
    },
  });

  const openAssignRound = (doc: UploadedDocument) => {
    setAssignRoundId(doc.production_round ? String(doc.production_round.round_id) : '');
    setAssignSupplement(String(doc.production_round?.supplement ?? 0));
    setAssignRoundDoc(doc);
  };

  const handleAssignRound = async () => {
    if (!assignRoundDoc) return;
    setErrorMsg(null);
    try {
      await assignRound.mutateAsync({
        docId: assignRoundDoc.id,
        roundId: assignRoundId ? Number(assignRoundId) : null,
        supplement: assignRoundId && Number(assignSupplement) > 0 ? Number(assignSupplement) : null,
      });
      setAssignRoundDoc(null);
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setErrorMsg(detail ?? 'Could not assign the production round.');
    }
  };

  // Rounds are per producing party — a client file can only join a
  // client-side round (the server enforces the same rule with a 422).
  const assignRoundOptions = useMemo(() => {
    if (!assignRoundDoc) return [];
    const party = (trackerData?.parties ?? []).find(
      (p) => p.producing_party === assignRoundDoc.source_party
    );
    return (party?.rounds ?? []).map((r) => ({
      value: String(r.id),
      label: r.label || `RFP ${r.round_number}`,
    }));
  }, [trackerData, assignRoundDoc]);

  const handleSaveBates = async () => {
    if (!batesEditDoc) return;
    setErrorMsg(null);
    try {
      await saveBates.mutateAsync({
        docId: batesEditDoc.id,
        start: batesEditStart.trim() || null,
        end: batesEditEnd.trim() || null,
      });
      setBatesEditDoc(null);
    } catch {
      setErrorMsg('Could not save the Bates range.');
    }
  };

  const openBatesEditor = (doc: UploadedDocument) => {
    setBatesEditStart(doc.bates?.start ?? '');
    setBatesEditEnd(doc.bates?.end ?? '');
    setBatesEditDoc(doc);
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) return;
    setErrorMsg(null);
    try {
      await deleteDocument.mutateAsync(deleteTarget.id);
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setErrorMsg(detail ?? 'Could not delete the document.');
    }
    setDeleteTarget(null);
  };

  const selectedDocs = useMemo(
    () => documents.filter((d) => selected.has(d.id)),
    [documents, selected]
  );
  const selectedStatementCount = selectedDocs.reduce((n, d) => n + d.statements.length, 0);

  const toggleSelected = (docId: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    setBulkBusy(true);
    setErrorMsg(null);
    const failures: string[] = [];
    // Sequential on purpose: each delete cascades through statements and
    // transactions server-side; parallel deletes just contend on the DB.
    for (const doc of selectedDocs) {
      try {
        await api.delete(`/documents/${doc.id}`);
      } catch (e) {
        const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
        failures.push(`${displayFilename(doc)}: ${detail ?? 'delete failed'}`);
      }
    }
    invalidateAfterDelete();
    setBulkBusy(false);
    setBulkConfirmOpen(false);
    setSelected(new Set());
    setSelectMode(false);
    if (failures.length > 0) setErrorMsg(failures.join(' · '));
  };

  const groups = useMemo((): { heading: string | null; docs: UploadedDocument[] }[] => {
    if (groupMode === 'recent') {
      return [{ heading: null, docs: documents }];
    }
    const byKey = new Map<string, UploadedDocument[]>();
    const add = (key: string, doc: UploadedDocument) => {
      const list = byKey.get(key) ?? [];
      if (!list.includes(doc)) list.push(doc);
      byKey.set(key, list);
    };
    if (groupMode === 'account') {
      for (const doc of documents) {
        const accounts = [...new Set(doc.statements.map(accountDisplay))];
        if (accounts.length === 0) add(docTypeGroup(doc.doc_type), doc);
        else accounts.forEach((a) => add(a, doc));
      }
    } else if (groupMode === 'type') {
      for (const doc of documents) add(docTypeGroup(doc.doc_type), doc);
    } else {
      for (const doc of documents) {
        add(getPartyLabel(doc.source_party, clientName, opposingPartyName), doc);
      }
    }
    return [...byKey.entries()]
      .sort(([a], [b]) =>
        groupMode === 'type'
          ? typeSectionRank(a) - typeSectionRank(b) || a.localeCompare(b)
          : a.localeCompare(b)
      )
      .map(([heading, docs]) => ({ heading, docs }));
  }, [documents, groupMode, clientName, opposingPartyName]);

  const openOriginal = async (doc: UploadedDocument) => {
    // Open the window synchronously (still inside the click's user activation)
    // and navigate it once the blob arrives — window.open AFTER an await gets
    // popup-blocked.
    const viewer = window.open('', '_blank');
    setBusyDocId(doc.id);
    try {
      const url = await fetchOriginal(doc.id, doc.content_type);
      if (viewer) viewer.location.href = url;
      else window.location.href = url;
    } catch {
      viewer?.close();
    } finally {
      setBusyDocId(null);
    }
  };

  const downloadOriginal = async (doc: UploadedDocument) => {
    setBusyDocId(doc.id);
    try {
      const url = await fetchOriginal(doc.id, doc.content_type);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename;
      a.click();
    } finally {
      setBusyDocId(null);
    }
  };

  if (isLoading) {
    return (
      <div role="status" aria-label="Loading documents" className="max-w-4xl space-y-2">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} variant="rect" height={84} className="w-full rounded-lg" />
        ))}
        <span className="sr-only">Loading documents</span>
      </div>
    );
  }
  if (error) {
    return <div className="text-danger py-12 text-center">Could not load documents.</div>;
  }
  if (documents.length === 0) {
    return (
      <Card padding="none">
        <EmptyState
          icon={FileText}
          title="No documents yet"
          description="Every file uploaded on the Upload tab is preserved here exactly as it was produced."
        />
      </Card>
    );
  }

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="t-card text-green-900">Uploaded Documents</h2>
          <p className="text-sm text-ink-soft">
            {documents.length} original{documents.length === 1 ? '' : 's'}, kept exactly as uploaded.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ToolbarChip
            selected={selectMode}
            onClick={() => {
              setSelectMode((v) => !v);
              setSelected(new Set());
            }}
          >
            {selectMode ? 'Done' : 'Select'}
          </ToolbarChip>
          <ToolbarGroup role="group" aria-label="Group documents">
            {GROUP_MODES.map(({ value, label, icon }) => (
              <ToolbarChip
                key={value}
                selected={groupMode === value}
                icon={icon}
                onClick={() => setGroupMode(value)}
              >
                {label}
              </ToolbarChip>
            ))}
          </ToolbarGroup>
        </div>
      </div>

      {errorMsg && (
        <div className="flex items-start gap-2 mb-4 px-3 py-2 rounded-lg border border-danger bg-danger-soft text-sm text-danger">
          <span className="flex-1">{errorMsg}</span>
          <IconButton
            icon={X}
            size="xs"
            variant="danger-ghost"
            aria-label="Dismiss error"
            onClick={() => setErrorMsg(null)}
            className="shrink-0"
          />
        </div>
      )}

      {selectMode && (
        <div className="flex items-center gap-3 mb-4 px-3 py-2 rounded-lg border border-border bg-surface-card text-sm">
          <span className="text-ink-soft">
            {selected.size} of {documents.length} selected
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              setSelected(
                selected.size === documents.length
                  ? new Set()
                  : new Set(documents.map((d) => d.id))
              )
            }
          >
            {selected.size === documents.length ? 'Clear selection' : 'Select all'}
          </Button>
          <div className="flex-1" />
          <Button
            variant="danger"
            size="md"
            icon={Trash2}
            loading={bulkBusy}
            disabled={selected.size === 0}
            onClick={() => setBulkConfirmOpen(true)}
          >
            Delete selected
          </Button>
        </div>
      )}

      <div className="space-y-6">
        {groups.map(({ heading, docs }) => (
          <div key={heading ?? '__all__'}>
            {heading && (
              <h3 className="t-eyebrow mb-2">
                {heading}
                <span className="ml-2 font-normal normal-case">({docs.length})</span>
              </h3>
            )}
            <div className="space-y-2">
              {docs.map((doc) => (
                <DocumentNode
                  key={doc.id}
                  doc={doc}
                  busy={busyDocId === doc.id}
                  clientName={clientName}
                  opposingPartyName={opposingPartyName}
                  selectMode={selectMode}
                  isSelected={selected.has(doc.id)}
                  onToggleSelect={() => toggleSelected(doc.id)}
                  onDelete={() => setDeleteTarget(doc)}
                  onOpen={() => openOriginal(doc)}
                  onDownload={() => downloadOriginal(doc)}
                  onOpenAccount={(bankLabel) =>
                    navigate(`/cases/${caseId}/investigation/transactions?bank=${encodeURIComponent(bankLabel)}`)
                  }
                  onEditBates={() => openBatesEditor(doc)}
                  onAssignRound={() => openAssignRound(doc)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <ConfirmModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirmed}
        title="Delete Document"
        message={
          deleteTarget
            ? `Permanently delete "${displayFilename(deleteTarget)}"? This removes the original file` +
              (deleteTarget.statements.length > 0
                ? ` and its ${deleteTarget.statements.length} extracted statement${
                    deleteTarget.statements.length === 1 ? '' : 's'
                  } with all their transactions`
                : '') +
              ' from the case. This cannot be undone.'
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        loading={deleteDocument.isPending}
      />

      <Modal
        open={!!batesEditDoc}
        onClose={() => setBatesEditDoc(null)}
        title="Bates Range"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setBatesEditDoc(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveBates} loading={saveBates.isPending}>
              {batesEditStart.trim() ? 'Save range' : 'Clear range'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-ink-soft">
            The as-produced page labels on{' '}
            <span className="font-medium text-green-900">
              {batesEditDoc ? displayFilename(batesEditDoc) : ''}
            </span>
            . Stamps are read automatically when the pages are legible — enter
            or correct the range here when they aren't. Leave both fields empty
            to clear it.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="First label"
              size="md"
              value={batesEditStart}
              onChange={(e) => setBatesEditStart(e.target.value)}
              placeholder="SMITH000001"
              className="t-ident"
            />
            <Input
              label="Last label"
              size="md"
              value={batesEditEnd}
              onChange={(e) => setBatesEditEnd(e.target.value)}
              placeholder="SMITH000483"
              className="t-ident"
            />
          </div>
        </div>
      </Modal>

      <Modal
        open={!!assignRoundDoc}
        onClose={() => setAssignRoundDoc(null)}
        title="Production Round"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setAssignRoundDoc(null)}>
              Cancel
            </Button>
            <Button onClick={handleAssignRound} loading={assignRound.isPending}>
              {assignRoundId ? 'Assign' : 'Remove from round'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-ink-soft">
            Which production round did{' '}
            <span className="font-medium text-green-900">
              {assignRoundDoc ? displayFilename(assignRoundDoc) : ''}
            </span>{' '}
            arrive in? Rounds live on Discovery → Production Tracker; only
            rounds produced by the same party are offered.
          </p>
          {assignRoundDoc && assignRoundOptions.length === 0 ? (
            <p className="text-sm text-warn">
              No production rounds exist yet for{' '}
              {getPartyLabel(assignRoundDoc.source_party, clientName, opposingPartyName)} — create
              one on the Production Tracker tab first.
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Select
                label="Round"
                size="md"
                value={assignRoundId}
                onChange={(e) => setAssignRoundId(e.target.value)}
                options={assignRoundOptions}
                placeholder="No round"
              />
              <Select
                label="Arrived as"
                size="md"
                value={assignSupplement}
                onChange={(e) => setAssignSupplement(e.target.value)}
                options={[
                  { value: '0', label: 'Original response' },
                  { value: '1', label: '1st supplement' },
                  { value: '2', label: '2nd supplement' },
                  { value: '3', label: '3rd supplement' },
                  { value: '4', label: '4th supplement' },
                ]}
                disabled={!assignRoundId}
              />
            </div>
          )}
        </div>
      </Modal>

      <ConfirmModal
        open={bulkConfirmOpen}
        onClose={() => setBulkConfirmOpen(false)}
        onConfirm={handleBulkDelete}
        title="Delete Selected Documents"
        message={
          `Permanently delete ${selectedDocs.length} document${selectedDocs.length === 1 ? '' : 's'}` +
          (selectedStatementCount > 0
            ? ` and their ${selectedStatementCount} extracted statement${
                selectedStatementCount === 1 ? '' : 's'
              } with all their transactions`
            : '') +
          '? The original files will be removed from the case. This cannot be undone.'
        }
        confirmLabel={`Delete ${selectedDocs.length === 1 ? 'document' : `${selectedDocs.length} documents`}`}
        variant="danger"
        loading={bulkBusy}
      />
    </div>
  );
}

function DocumentNode({
  doc, busy, clientName, opposingPartyName, selectMode, isSelected,
  onToggleSelect, onDelete, onOpen, onDownload, onOpenAccount, onEditBates,
  onAssignRound,
}: {
  doc: UploadedDocument;
  busy: boolean;
  clientName?: string | null;
  opposingPartyName?: string | null;
  selectMode: boolean;
  isSelected: boolean;
  onToggleSelect: () => void;
  onDelete: () => void;
  onOpen: () => void;
  onDownload: () => void;
  onOpenAccount: (bankLabel: string) => void;
  onEditBates: () => void;
  onAssignRound: () => void;
}) {
  const isImage = (doc.content_type ?? '').startsWith('image/');
  const FileIcon = isImage ? ImageIcon : FileText;
  const pendingProposals = doc.proposals.filter((p) => p.status === 'pending');
  const [showAllStatements, setShowAllStatements] = useState(false);
  const orderedStatements = sortedStatements(doc.statements);
  const collapsed =
    !showAllStatements && orderedStatements.length > STATEMENT_COLLAPSE_THRESHOLD;
  const visibleStatements = collapsed
    ? orderedStatements.slice(0, STATEMENT_COLLAPSED_COUNT)
    : orderedStatements;


  return (
    <Card className={cn(isSelected && 'ring-2 ring-green-800')}>
      <div className="flex items-start gap-3">
        {selectMode && (
          /* eslint-disable-next-line no-restricted-syntax -- native checkbox: no ui/Checkbox primitive exists yet */
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            className="mt-1 w-4 h-4 shrink-0 accent-green-800 cursor-pointer"
            aria-label={`Select ${doc.filename}`}
          />
        )}
        <FileIcon size={20} className="mt-px shrink-0 text-green-700" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-green-900 truncate" title={doc.filename}>
              {displayFilename(doc)}
            </span>
            <Tag tone="info" className="shrink-0">{docTypeLabel(doc.doc_type)}</Tag>
            <PartyBadge party={doc.source_party} clientName={clientName} opposingPartyName={opposingPartyName} size="xs" />
            {doc.bates?.start && (
              <Tag
                tone="neutral"
                className="shrink-0"
                title={
                  doc.bates.source === 'manual'
                    ? 'Bates range (entered at upload)'
                    : doc.bates.source === 'derived'
                      ? 'Bates range (from the parent production)'
                      : 'Bates range (read from the page stamps)'
                }
              >
                <span className="t-ident">{batesRangeText(doc.bates)}</span>
              </Tag>
            )}
            {(doc.bates?.gaps?.length ?? 0) > 0 && (
              <Tag
                tone="warn"
                className="shrink-0"
                title={`Not in this production: ${(doc.bates?.gaps ?? [])
                  .map(([a, b]) => `${a}–${b}`)
                  .join(', ')} — the stamp sequence skips these numbers.`}
              >
                Bates gap
              </Tag>
            )}
            {doc.bates?.detected_differs && (
              <Tag
                tone="warn"
                className="shrink-0"
                title={`Stamps on the pages read ${doc.bates.detected_differs}, which differs from the entered range.`}
              >
                Bates mismatch
              </Tag>
            )}
            {doc.production_round && (
              <Tag
                tone="neutral"
                className="shrink-0"
                title="Production round this document was produced in (Discovery → Production Tracker)"
              >
                {productionRoundText(doc.production_round)}
              </Tag>
            )}
          </div>
          <div className="text-xs text-ink-soft mt-1 flex items-center gap-2">
            <span>Uploaded {formatUploadedAt(doc.uploaded_at)}</span>
            {doc.size_bytes ? <span>· {formatSize(doc.size_bytes)}</span> : null}
            {doc.statements.length > 1 && (
              <span className="inline-flex items-center gap-1 text-green-800">
                <Layers size={12} aria-hidden="true" /> split into {doc.statements.length} statements
              </span>
            )}
            {UUID_NAME_RE.test(doc.filename) && (
              <span className="text-ink-faint" title={doc.filename}>· file: {doc.filename.slice(0, 8)}…</span>
            )}
          </div>

          {(doc.statements.length > 0 || doc.proposals.length > 0) && (
            <div className="mt-2 border-l-2 border-cream-dark pl-3 space-y-1">
              {visibleStatements.map((s) => {
                const recon = reconciliationBadge(s.reconciliation_status);
                return (
                  /* eslint-disable-next-line no-restricted-syntax -- full-width statement row link: no ui primitive fits this layout */
                  <button
                    key={s.id}
                    onClick={() => s.bank_label && onOpenAccount(s.bank_label)}
                    className="flex items-center gap-2 text-sm text-ink hover:text-green-800 w-full text-left group"
                    title="Open this account's transactions"
                  >
                    <span className="font-medium truncate">{accountDisplay(s)}</span>
                    <span className="text-ink-soft shrink-0">{statementPeriod(s)}</span>
                    {s.status === 'needs_review' && <Tag tone="warn" className="shrink-0">Needs review</Tag>}
                    {s.status !== 'needs_review' && recon && (
                      <Tag tone={recon.variant === 'warning' ? 'warn' : 'danger'} className="shrink-0">
                        {recon.label}
                      </Tag>
                    )}
                    <ExternalLink size={12} className="opacity-0 group-hover:opacity-60 shrink-0" aria-hidden="true" />
                  </button>
                );
              })}
              {orderedStatements.length > STATEMENT_COLLAPSE_THRESHOLD && (
                /* eslint-disable-next-line no-restricted-syntax -- inline disclosure toggle: no ui primitive fits this layout */
                <button
                  onClick={() => setShowAllStatements(v => !v)}
                  className="text-xs font-medium text-green-800 hover:text-green-900 underline underline-offset-2"
                >
                  {showAllStatements
                    ? 'Show fewer'
                    : `Show all ${doc.statements.length} statements`}
                </button>
              )}
              {doc.proposals.map((p) => (
                <div key={p.id} className="flex items-center gap-2 text-sm text-ink">
                  <span>{docTypeLabel(p.doc_type)}</span>
                  {p.status === 'pending' && <Tag tone="warn">Awaiting review</Tag>}
                  {p.status === 'confirmed' && <Tag tone="good">Applied</Tag>}
                  {p.status === 'dismissed' && <Tag tone="unknown">Dismissed</Tag>}
                </div>
              ))}
            </div>
          )}
          {doc.statements.length === 0 && pendingProposals.length === 0 && doc.proposals.length === 0 && (
            <div className="mt-1 text-xs text-ink-faint">Not yet processed into case data.</div>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {busy ? (
            <Loader2 size={16} className="animate-spin text-ink-soft" aria-hidden="true" />
          ) : (
            <>
              {doc.has_original ? (
                <>
                  <IconButton
                    icon={ExternalLink}
                    size="sm"
                    variant="ghost"
                    aria-label="View the original file"
                    onClick={onOpen}
                    className="text-ink-soft hover:text-green-800"
                  />
                  <IconButton
                    icon={Download}
                    size="sm"
                    variant="ghost"
                    aria-label="Download the original file"
                    onClick={onDownload}
                    className="text-ink-soft hover:text-green-800"
                  />
                </>
              ) : (
                <span className="text-xs text-ink-faint" title="This file predates the document registry and its original was not preserved.">
                  Original unavailable
                </span>
              )}
              {!selectMode && (
                <>
                  <IconButton
                    icon={Stamp}
                    size="sm"
                    variant="ghost"
                    aria-label={doc.bates?.start ? 'Edit the Bates range' : 'Enter a Bates range'}
                    onClick={onEditBates}
                    className="text-ink-soft hover:text-green-800"
                  />
                  {(doc.source_party === 'client' ||
                    doc.source_party === 'opposing' ||
                    doc.source_party === 'third_party') && (
                    <IconButton
                      icon={FolderInput}
                      size="sm"
                      variant="ghost"
                      aria-label={
                        doc.production_round
                          ? 'Change the production round'
                          : 'Assign to a production round'
                      }
                      onClick={onAssignRound}
                      className="text-ink-soft hover:text-green-800"
                    />
                  )}
                  <IconButton
                    icon={Trash2}
                    size="sm"
                    variant="danger-ghost"
                    aria-label="Delete this document and everything extracted from it"
                    onClick={onDelete}
                    className="text-ink-soft hover:text-danger"
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
    </Card>
  );
}
